/**
 * Auth HTTP Interceptor
 *
 * Dual-mode authentication for API requests:
 * - Local dev (useEmulators: true): Fetches dev token from backend
 * - Production/Preview: Uses Firebase ID token from authenticated user
 * - Fallback: Static API key if dev-token fetch fails (backward compatibility)
 *
 * Dev tokens are 24-hour session tokens provided by the backend's
 * /api/v1/auth/dev-token endpoint (development only).
 * 
 * Token Refresh on 401:
 * - Automatically refreshes expired tokens when receiving 401 errors
 * - For Firebase tokens: Uses Firebase's built-in refresh mechanism
 * - For session tokens: Calls /api/v1/auth/refresh with Firebase ID token
 * - Retries the original request once with refreshed token
 */
import {
    HttpInterceptorFn,
    HttpRequest,
    HttpHandlerFn,
    HttpEvent,
    HttpErrorResponse,
} from '@angular/common/http';
import { Observable, from, of, switchMap, catchError, throwError, BehaviorSubject, filter, take } from 'rxjs';
import { inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { environment } from '../../../environments/environment';
import { isConfiguredApiUrl } from './api-url.util';

// Cached dev token (24h validity)
let cachedDevToken: { token: string; expiresAt: number } | null = null;
// Pending fetch promise to deduplicate concurrent requests
let pendingFetch: Promise<string | null> | null = null;
// Token refresh state
let isRefreshing = false;
let refreshTokenSubject = new BehaviorSubject<string | null>(null);

function isLocalHost(hostname: string): boolean {
    if (['localhost', '127.0.0.1', '::1'].includes(hostname)) return true;
    if (hostname.startsWith('10.')) return true;
    if (hostname.startsWith('192.168.')) return true;
    if (/^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname)) return true;
    if (/^100\.(6[4-9]|[7-9]\d|1[01]\d|12[0-7])\./.test(hostname)) return true;
    return false;
}

function shouldUseDevTokenFlow(): boolean {
    if (!environment.useEmulators || environment.deploymentEnv !== 'local') {
        return false;
    }

    try {
        const apiUrl = new URL(environment.api.baseUrl);
        return isLocalHost(apiUrl.hostname);
    } catch {
        // If URL parsing fails, stay safe and avoid forcing dev-token flow.
        return false;
    }
}

/**
 * Clear the cached dev token (useful when user changes)
 */
export function clearDevTokenCache(): void {
    cachedDevToken = null;
    pendingFetch = null;
    console.log('[authInterceptor] Dev token cache cleared');
}

/**
 * Functional HTTP interceptor that adds Authorization header
 * and handles 401 errors with automatic token refresh
 */
export const authInterceptor: HttpInterceptorFn = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    // Skip requests not going to any of our configured API hosts
    if (!isConfiguredApiUrl(req.url)) {
        return next(req);
    }

    // Skip auth for dev-token endpoint (prevent infinite loop)
    if (req.url.includes('/api/v1/auth/dev-token')) {
        return next(req);
    }

    // Skip auth for refresh endpoint (prevent infinite loop)
    if (req.url.includes('/api/v1/auth/refresh')) {
        return next(req);
    }

    // Local dev mode: Fetch dev token from backend
    if (shouldUseDevTokenFlow()) {
        return from(getDevToken()).pipe(
            // Only catch errors from the token fetch itself. If the token fetch
            // fails, fall back to sending the request without a token rather than
            // retrying it (which would cause a spurious 401 on top of the real error).
            catchError((error) => {
                console.error('[authInterceptor] Error getting dev token:',
                    error?.message || error?.statusText || JSON.stringify(error) || error);
                return of(null);
            }),
            switchMap((token) => {
                if (!token) {
                    console.warn('[authInterceptor] No dev token available');
                    return next(req);
                }

                const authReq = req.clone({
                    setHeaders: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                return next(authReq).pipe(
                    catchError((error: HttpErrorResponse) => {
                        // Handle 401 by attempting token refresh
                        if (error.status === 401) {
                            return handleDevToken401Error(req, next);
                        }
                        return throwError(() => error);
                    })
                );
            })
        );
    }

    // Production/Preview mode: Use Firebase ID token
    const auth = inject(Auth);

    return from(getIdToken(auth)).pipe(
        // Only catch errors from the token fetch itself. If the token fetch
        // fails, fall back to sending the request without a token rather than
        // retrying it (which would cause a spurious 401 on top of the real error).
        catchError((error) => {
            console.error('[authInterceptor] Error getting ID token:', error);
            return of(null);
        }),
        switchMap((token) => {
            if (!token) {
                // No token available - proceed without Authorization header
                return next(req);
            }

            // Add Firebase ID token to request
            const authReq = req.clone({
                setHeaders: {
                    Authorization: `Bearer ${token}`,
                },
            });

            return next(authReq).pipe(
                catchError((error: HttpErrorResponse) => {
                    // Handle 401 by attempting token refresh
                    if (error.status === 401) {
                        return handleFirebaseToken401Error(req, next, auth);
                    }
                    return throwError(() => error);
                })
            );
        })
    );
};

/**
 * Get Firebase ID token for the current user
 */
async function getIdToken(auth: Auth): Promise<string | null> {
    try {
        const user = auth.currentUser;
        if (!user) {
            return null;
        }
        return await user.getIdToken();
    } catch (error) {
        console.error('[authInterceptor] Error getting ID token:', error);
        return null;
    }
}

/**
 * Get development token from backend
 * Caches the token for 24 hours to minimize API calls
 * Uses deduplication to prevent concurrent requests from racing
 * Falls back to static API key if fetch fails
 */
async function getDevToken(): Promise<string | null> {
    // Return cached token if still valid (with 5min buffer)
    const now = Date.now();
    const bufferMs = 5 * 60 * 1000; // 5 minutes

    if (cachedDevToken && now < cachedDevToken.expiresAt - bufferMs) {
        return cachedDevToken.token;
    }

    // If there's already a pending fetch, wait for it instead of making another request
    if (pendingFetch) {
        return pendingFetch;
    }

    // Start a new fetch and store the promise for deduplication
    pendingFetch = fetchDevToken();

    try {
        const result = await pendingFetch;
        return result;
    } finally {
        // Clear pending fetch when done
        pendingFetch = null;
    }
}

/**
 * Actually fetch the dev token from the backend.
 * Retries a few times with a short backoff first — on a fresh `dev:orchestrated`
 * run the frontend can compile and load before the backend finishes its DB/CMS
 * startup, so the first attempt failing with a network error is expected.
 */
async function fetchDevToken(): Promise<string | null> {
    const testEmail = (environment as any).testUser?.email || 'admin@test.local';
    const url = `${environment.api.baseUrl}/api/v1/auth/dev-token?email=${encodeURIComponent(testEmail)}`;
    const retryDelaysMs = [500, 1000, 2000];

    for (let attempt = 0; attempt <= retryDelaysMs.length; attempt++) {
        try {
            console.log('[authInterceptor] Fetching dev token from:', url, `(attempt ${attempt + 1})`);
            const response = await fetch(url);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('[authInterceptor] Failed to fetch dev token:', response.status, errorText);
                break; // Non-network error (e.g. 4xx/5xx) — retrying won't help
            }

            const data = await response.json();

            if (!data.success || !data.data?.token) {
                console.error('[authInterceptor] Invalid dev token response:', data);
                break;
            }

            // Cache the token
            cachedDevToken = {
                token: data.data.token,
                expiresAt: data.data.expiresAt,
            };

            console.log('[authInterceptor] Dev token fetched successfully (24h validity)');
            return cachedDevToken.token;
        } catch (error) {
            // Network error (e.g. "Failed to fetch") — backend likely still starting up
            console.warn('[authInterceptor] Error fetching dev token (will retry if attempts remain):', error);
            if (attempt < retryDelaysMs.length) {
                await new Promise((r) => setTimeout(r, retryDelaysMs[attempt]));
                continue;
            }
        }
    }

    console.error('[authInterceptor] Giving up on dev token after retries; falling back to static API key');
    return getFallbackApiKey();
}

/**
 * Fallback to static API key for backward compatibility
 */
function getFallbackApiKey(): string | null {
    if ('apiKey' in environment.api) {
        console.log('[authInterceptor] Falling back to static API key');
        return (environment.api as { baseUrl: string; apiKey: string }).apiKey;
    }
    return null;
}

/**
 * Handle 401 error in dev mode by refreshing the dev token
 */
function handleDevToken401Error(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
    if (isRefreshing) {
        // Wait for the refresh to complete
        return refreshTokenSubject.pipe(
            filter((token) => token !== null),
            take(1),
            switchMap((token) => {
                const authReq = req.clone({
                    setHeaders: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                return next(authReq);
            })
        );
    }

    isRefreshing = true;
    refreshTokenSubject.next(null);

    console.warn('[authInterceptor] 401 error - attempting token refresh...');

    // Clear cached token and fetch fresh one
    cachedDevToken = null;
    pendingFetch = null;

    return from(getDevToken()).pipe(
        switchMap((newToken) => {
            isRefreshing = false;

            if (!newToken) {
                console.error('[authInterceptor] Token refresh failed');
                refreshTokenSubject.next(null);
                return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Unauthorized' }));
            }

            console.log('[authInterceptor] Token refreshed successfully');
            refreshTokenSubject.next(newToken);

            // Retry original request with new token
            const authReq = req.clone({
                setHeaders: {
                    Authorization: `Bearer ${newToken}`,
                },
            });

            return next(authReq);
        }),
        catchError((error) => {
            isRefreshing = false;
            refreshTokenSubject.next(null);
            console.error('[authInterceptor] Token refresh error:', error);
            return throwError(() => error);
        })
    );
}

/**
 * Handle 401 error in production by refreshing Firebase token
 * and requesting new session token from backend
 */
function handleFirebaseToken401Error(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
    auth: Auth
): Observable<HttpEvent<unknown>> {
    if (isRefreshing) {
        // Wait for the refresh to complete
        return refreshTokenSubject.pipe(
            filter((token) => token !== null),
            take(1),
            switchMap((token) => {
                const authReq = req.clone({
                    setHeaders: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                return next(authReq);
            })
        );
    }

    isRefreshing = true;
    refreshTokenSubject.next(null);

    console.warn('[authInterceptor] 401 error - attempting token refresh...');

    return from(refreshFirebaseToken(auth)).pipe(
        switchMap((newToken) => {
            isRefreshing = false;

            if (!newToken) {
                console.error('[authInterceptor] Token refresh failed');
                refreshTokenSubject.next(null);
                return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Unauthorized' }));
            }

            console.log('[authInterceptor] Token refreshed successfully');
            refreshTokenSubject.next(newToken);

            // Retry original request with new token
            const authReq = req.clone({
                setHeaders: {
                    Authorization: `Bearer ${newToken}`,
                },
            });

            return next(authReq);
        }),
        catchError((error) => {
            isRefreshing = false;
            refreshTokenSubject.next(null);
            console.error('[authInterceptor] Token refresh error:', error);
            return throwError(() => error);
        })
    );
}

/**
 * Refresh Firebase token and get new session token from backend
 */
async function refreshFirebaseToken(auth: Auth): Promise<string | null> {
    try {
        const user = auth.currentUser;
        if (!user) {
            console.error('[authInterceptor] No current user for token refresh');
            return null;
        }

        // Force refresh Firebase ID token
        console.log('[authInterceptor] Refreshing Firebase ID token...');
        const freshIdToken = await user.getIdToken(true);

        // Request new session token from backend
        console.log('[authInterceptor] Requesting new session token from backend...');
        const response = await fetch(`${environment.api.baseUrl}/api/v1/auth/refresh`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${freshIdToken}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('[authInterceptor] Token refresh failed:', response.status, errorText);
            return null;
        }

        const data = await response.json();

        if (!data.success || !data.data?.token) {
            console.error('[authInterceptor] Invalid refresh response:', data);
            return null;
        }

        console.log('[authInterceptor] New session token received');
        return data.data.token;
    } catch (error) {
        console.error('[authInterceptor] Error refreshing Firebase token:', error);
        return null;
    }
}
